import { useCallback, useContext, useEffect, useRef, useState } from "react"

import { graphql, useStaticQuery } from "gatsby"
import gsap from "gsap/all"
import styled, { css } from "styled-components"

import { ReactComponent as Chevron } from "images/global/chevron-right.svg"
import { ReactComponent as LinkedInSVG } from "images/logos/linkedin.svg"
import { ReactComponent as TwitterSVG } from "images/logos/twitter.svg"
import { ReactComponent as YoutubeSVG } from "images/logos/youtube.svg"
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import { LocaleData } from "types/gatsby-alias"
import { fresponsive } from "utils/fullyResponsive"
import { Opacity } from "utils/functions"
import useGetStaticTranslations from "utils/hooks/useGetStaticTranslations"
import useMedia from "utils/useMedia"
import links from "utils/links"
import { useI18next } from "gatsby-plugin-react-i18next"
import BurgerButton from "./buttons/BurgerButton"
import Button from "./buttons/Button"
import LanguageButton from "./buttons/LanguageButton"
import { data } from "./Header"
import HeaderPopoutBox from "./header/HeaderPopoutBox"
import HeaderPopoutBoxMobile from "./header/HeaderPopoutBoxMobile"
import { Lightmode, ShowHamburger } from "./Providers"
import  { checkedLocaleLink } from "utils/routes"
import HeroLogo from "./header/HeroLogo"

interface Props {
  pathname: string
  languageOverride?: string
}

export default function HeaderBurger({ pathname, languageOverride }: Props) {
  const [burgerMode, setBurgerMode] = useState<"none" | "closed" | "open">(
    "none"
  ) // 3 possible states: none, closed, open
  const [open, setOpen] = useState(false)
  const [keepOpen, setKeepOpen] = useState(false)
  const [tab, setTab] = useState(0)
  const sliderRef = useRef<HTMLDivElement>(null)
  const burgerRef = useRef<HTMLDivElement>(null)
  const { lightmode } = useContext(Lightmode)
  const { show } = useContext(ShowHamburger)
  const mediaVal = useMedia(true, true, true, false)
  const toggle = useMedia(true, true, true, false)
  const [showFollowDiv, setShowFollowDiv] = useState(true)
  const { language } = useI18next()
  const handleToggleFollowDiv = () => {
    setShowFollowDiv(!showFollowDiv)
  }

  const localeDataJS: LocaleData = useGetStaticTranslations(
    "menu",
    languageOverride
  )

  const toggleBurgerButton = (shrinkOrGrow: "shrink" | "grow" | undefined) => {
    if (shrinkOrGrow === "shrink")
      gsap.to(burgerRef.current, {
        scale: 0,
        duration: 0.3,
        ease: easing.button,
      })
    else
      gsap.to(burgerRef.current, {
        scale: 1,
      })
  }

  const toggleSlider = useCallback(() => {
    if (burgerMode !== "open")
      gsap.to(sliderRef.current, {
        duration: 0.3,
        x: "100%",
        ease: easing.button,
      })
    else
      gsap.to(sliderRef.current, {
        duration: 0.3,
        x: 0,
        ease: easing.button,
      })
  }, [burgerMode])

  useEffect(() => {
    if (toggle) {
      toggleSlider()
    }

    setOpen(false)
  }, [burgerMode, toggle, toggleSlider])

  useEffect(() => {
    let alwaysBurgerPage = false
    // always start on burger if in mobile
    if (!toggle) {
      alwaysBurgerPage = true
    }
    // pages to start with burger on
    const ArrayOfPagesToStartWithBurgerModeOn = [
      "#blog-or-news-post",
      "#landing_page",
      "#event-post",
      "#landing_nav",
      "#contact",
      "#privacy-policy",
      "#privacy-policy-california",
    ]
    ArrayOfPagesToStartWithBurgerModeOn.forEach(pageId => {
      if (document.querySelector(pageId)) {
        toggleBurgerButton("grow")
        alwaysBurgerPage = true
      }
    })

    if (mediaVal && !alwaysBurgerPage) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: "#nav",
          start: 0,
          end: "100%",
          onEnter: () => {
            toggleBurgerButton("shrink")
          },
          onEnterBack: () => {
            toggleBurgerButton("shrink")
            setBurgerMode("none")
          },
          onLeave: () => {
            toggleBurgerButton("grow")
          },
        },
      })
      return () => {
        tl.kill()
      }
    }
  }, [mediaVal, toggle])

  const categories = () => {
    return data.map((el, i) => (
      <Category
        key={el.title}
        color={!open || tab === i ? colors.white : Opacity(colors.white, 0.5)}
        onClick={() => {
          setKeepOpen(true)
        }}
        onMouseEnter={() => {
          setTab(i)
          setOpen(true)
          setKeepOpen(false)
        }}
      >
        <ChevronWrapper
          tab={tab}
          i={i}
          open={open}
          color={!open || tab === i ? colors.white : Opacity(colors.white, 0.5)}
        >
          <Chevron />
        </ChevronWrapper>
        <CategoryText>{localeDataJS[el.title]}</CategoryText>
      </Category>
    ))
  }

  return (
    <>
      <CloseBox
        open={burgerMode === "open"}
        onMouseEnter={() => !keepOpen && setOpen(false)}
        onKeyUp={() => {
          setKeepOpen(false)
          setOpen(false)
          setBurgerMode("closed")
        }}
        onClick={() => {
          setKeepOpen(false)
          setOpen(false)
          setBurgerMode("closed")
        }}
        role="button"
        tabIndex={0}
      />
      <Wrapper>
        <Absolute id="headerBurger_open-close" show={show} ref={burgerRef}>
          <BurgerButton
            lightMode={lightmode}
            burgerMode={burgerMode}
            onClick={() =>
              setBurgerMode(burgerMode === "open" ? "closed" : "open")
            }
            languageOverride={languageOverride}
          />
        </Absolute>

        <HeaderPopoutBoxMobile
          data={data}
          burgerMode={burgerMode}
          pathname={pathname}
          translation={localeDataJS}
          languageOverride={languageOverride}
        />
        <Slider ref={sliderRef}>
          <CloseBoxInSlider onMouseEnter={() => !keepOpen && setOpen(false)} />
          <Title>
            <TitleText>{localeDataJS.navigation}</TitleText>
            <Line />
          </Title>
          <Nav
            id="nav-burger"
            onClick={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
            role="button"
            tabIndex={0}
            >
            <Categories>
              {categories()}
              <HeaderPopoutBox
                tab={data[tab]}
                index={tab}
                open={open}
                translation={localeDataJS}
                languageOverride={languageOverride}
                />
            </Categories>

            <Button
              onClick={e => {
                e.preventDefault();
                window.open(checkedLocaleLink(links.login, language), "_blank")
                setBurgerMode(burgerMode !== "none" ? "none" : "closed")
              }}
              >
              {localeDataJS.Login}
            </Button>
          </Nav>
          <BurgerFooter>
            <LanguageButton
              handleToggleFollowDiv={handleToggleFollowDiv}
              pathname={pathname}
              languageOverride={languageOverride}
            />
            {/* <HeroLogo/> */}
            {showFollowDiv && (
              <Follow>
                <Text>{localeDataJS["Follow Galileo"]}</Text>
                <Socials>
                  <a
                    aria-label="twitter"
                    target="_blank"
                    href="https://twitter.com/galileofintech"
                    rel="noreferrer"
                  >
                    <TwitterSVG />
                  </a>
                  <a
                    aria-label="You Tube"
                    target="_blank"
                    href="https://www.youtube.com/c/GalileoFinancialTechnologies"
                    rel="noreferrer"
                  >
                    <YoutubeSVG />
                  </a>
                  <a
                    aria-label="Linkedin"
                    target="_blank"
                    href="https://www.linkedin.com/company/galileo-financial-technologies/"
                    rel="noreferrer"
                  >
                    <LinkedInSVG />
                  </a>
                </Socials>
              </Follow>
            )}
          </BurgerFooter>
        </Slider>
      </Wrapper>
    </>
  )
}

const Category = styled.button`
  ${media.fullWidth} {
    margin-bottom: 22px;
  }
  ${media.tablet} {
    margin-bottom: 2.5vw;
  }
  margin-bottom: 1.408vw;
  transition-timing-function: ${easing.button};
  transition-duration: 0.3s;
  cursor: pointer;
  color: ${props => props.color};
  border: none;
  background-color: transparent;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
`

const ChevronWrapper = styled.div<{ tab: number; open: boolean; i: number }>`
  ${media.fullWidth} {
    width: 15px;
    height: 15px;
  }
  ${media.tablet} {
    width: 1.465vw;
    height: 1.465vw;
  }
  width: 0.904vw;
  height: 0.904vw;
  transition-timing-function: ${easing.button};
  transition-duration: 0.3s;
  display: flex;
  transform: ${props =>
    props.i === props.tab && props.open ? `rotate(180deg);` : `rotate(0deg);`};
  path {
    transition-timing-function: ${easing.button};
    transition-duration: 0.3s;
    stroke: ${props => props.color};
  }
`

const CategoryText = styled.p`
  ${media.fullWidth} {
    margin-left: 10px;
    line-height: 20.8px;
  }
  ${media.tablet} {
    margin-left: 0.977vw;
    line-height: 2.031vw;
  }
  display: flex;
  margin-left: 0.602vw;
  line-height: 1.253vw;
  text-transform: capitalize;
  ${text.buttonR}
`

const CloseBox = styled.div<{ open: boolean }>`
  ${media.mobile} {
    display: none;
  }
  display: ${props => (props.open ? "flex" : "none")};
  position: fixed;
  top: 0;
  width: 100%;
  height: 100vh;
  background-color: ${Opacity(colors.blue700, 0.8)};
  z-index: 20;
  min-height: 100vh;
`

const Wrapper = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column; /* This will stack the child elements vertically */
  justify-content: center;
  position: fixed;
  z-index: 21;
`
const Absolute = styled.div<{ show: boolean }>`
  display: ${({ show }) => (show ? "inline-block" : "none")};
  transition: display 0.4s;
  ${fresponsive(css`
    position: absolute;
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    right: 110px;
  `)}
  ${media.mobile} {
    right: 5.333vw;
    top: 4vw;
    transform: scale(1);
  }
  top: 3vw;
  z-index: 2;
  transform: scale(0);
`

const Slider = styled.div`
  ${fresponsive(css`
    width: 358px;
    height: 100vh;
    flex-shrink: 0;
    background-color: ${colors.blue500};
    z-index: 1;
    position: fixed;
    top: 0;
    right: 0;
    padding: 20px;
  `)}
  ${media.mobile} {
    display: none;
  }
  transform: translateX(100%);
`

const CloseBoxInSlider = styled.div`
  position: absolute;
  z-index: -1;
  ${fresponsive(css`
    width: 368px;
    height: 1366px;
    flex-shrink: 0;
  `)}
`
const Title = styled.div`
  ${fresponsive(css`
    display: flex;
    width: 218px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    margin-top: 150px;
  `)}
`

const TitleText = styled.div`
  color: ${colors.blue100};
  ${fresponsive(css`
    ${text.captionKicker}
    text-transform: uppercase;
  `)}
`
const Line = styled.div`
  ${fresponsive(css`
    width: 218px;
    height: 1px;
    border-radius: 1px;
    background: ${colors.blue100};
  `)}
`

const Nav = styled.div`
  ${fresponsive(css`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    // gap: 35px;
    margin-top: 40px;
    padding-left: 40px;
    justify-content: center;
    z-index: 2;
    position: fixed;
  `)}
`

const Categories = styled.div`
  position: relative;
`

const BurgerFooter = styled.div`
  ${media.fullWidth} {
    bottom: 25px;
    margin-left: 40px;
  }
  ${media.tablet} {
    margin-left: 3.906vw;
    bottom: 2.13vw;
  }
  height: 122px;
  display: flex;
  gap: 25px;
  flex-direction: column;
  align-items: flex-start;
  justify-items: flex-start;
  padding: 0;
  position: absolute;
  margin-left: 2.41vw;
  bottom: 2.019vw;
`

const Follow = styled.div`
  ${fresponsive(css`
    display: flex;
    flex-direction: column;
    gap: 10px;
  `)}
`

const Text = styled.p`
  color: ${colors.blue100};

  ${fresponsive(css`
    font-family: Plain;
    font-size: 15px;
    font-style: normal;
    font-weight: 300;
    text-transform: uppercase;
    align-items: center;
  `)}
`

const Socials = styled.div`
  svg {
    ${media.fullWidth} {
      width: 25px;
      margin-right: 10px;
      margin-top: 10px;
    }
    ${media.tablet} {
      width: 2.441vw;
      margin-right: 0.977vw;
      margin-top: 0.977vw;
    }
    width: 1.506vw;
    margin-right: 0.602vw;
    margin-top: 0.602vw;
    ${media.hover} {
      :hover {
        rect {
          fill: white;
        }
      }
    }
  }
`
